/**
 * Social authentication buttons for OAuth providers (Microsoft only)
 */
import { Box, Button, Divider, Typography } from "@mui/material";
import { useAuthContext, SocialProvider } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";
import { BRAND_COLORS } from "./AuthLayout";

// Translation constants for extraction
const CONTINUE_WITH_TEXT = gettext("Continue with");
const OR_TEXT = gettext("or");

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

function getProviderLogo(provider: string) {
  switch (provider.toLowerCase()) {
    case "microsoft":
      return <MicrosoftLogo />;
    default:
      return null;
  }
}

interface SocialAuthButtonsProps {
  showDivider?: boolean;
}

export function SocialAuthButtons({
  showDivider = true,
}: SocialAuthButtonsProps) {
  const { socialProviders, csrfToken } = useAuthContext();

  if (!socialProviders || socialProviders.length === 0) {
    return null;
  }

  const handleSocialLogin = (provider: SocialProvider) => {
    // Create and submit form for social auth
    const form = document.createElement("form");
    form.method = "POST";
    form.action = provider.loginUrl;

    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "csrfmiddlewaretoken";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <Box
      sx={{
        padding: "0 2.5rem 1.5rem",
        backgroundColor: "white",
      }}
    >
      {socialProviders.map((provider) => (
        <Button
          key={provider.provider}
          fullWidth
          variant="outlined"
          onClick={() => handleSocialLogin(provider)}
          sx={{
            padding: "0.875rem 1.5rem",
            border: `1px solid ${BRAND_COLORS.border}`,
            borderRadius: "8px",
            backgroundColor: "white",
            color: BRAND_COLORS.text.primary,
            fontSize: "0.9375rem",
            fontWeight: 500,
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            marginBottom: "0.75rem",
            fontFamily: "'Montserrat', sans-serif",
            "&:hover": {
              backgroundColor: "#F8F9FA",
              borderColor: BRAND_COLORS.inputFocus,
            },
          }}
          data-testid={`social-auth-${provider.provider}`}
        >
          {getProviderLogo(provider.provider)}
          {CONTINUE_WITH_TEXT} {provider.name}
        </Button>
      ))}

      {showDivider && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: "1.5rem",
          }}
        >
          <Divider sx={{ flex: 1 }} />
          <Typography
            sx={{
              padding: "0 1rem",
              color: BRAND_COLORS.text.muted,
              fontSize: "0.875rem",
            }}
          >
            {OR_TEXT}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>
      )}
    </Box>
  );
}
